import { useLeaseCompData } from "@hooks/useLeaseCompData";
import { useLeaseCompFieldRules } from "@hooks/useLeaseCompFieldRules";
import { useSurvey } from "@hooks/useSurveySelectors";
import type { LeaseCompFieldRule } from "@survey-types/survey.core.types";
import type { LeaseComp } from "@survey-types/survey.leaseComp.types";

export interface UseLeaseComparisonDataReturn {
  leaseComps: LeaseComp[];
  visibleFields: LeaseCompFieldRule[];
  maxLeaseComps: number;
  leaseCompOrders: Record<string, string>;
  mode: "Landscape" | "Portrait";
  showPropertyPhoto: boolean;
}

/**
 * Hook to orchestrate data for lease comparison component
 *
 * Handles:
 * - Fetching lease comps and field rules
 * - Filtering hidden lease comps
 * - Filtering and sorting visible fields
 * - Calculating max lease comps per page based on orientation
 * - Building lease comp order numbers
 *
 * @returns {UseLeaseComparisonDataReturn} Processed data ready for pagination
 */
export const useLeaseComparisonData = (): UseLeaseComparisonDataReturn => {
  // Load survey for paper size
  const survey = useSurvey();

  // Load lease comp data from RTK Query
  const { leaseComps: rawLeaseComps } = useLeaseCompData();

  // Load field rules from Redux
  const allFieldRules = useLeaseCompFieldRules();

  // Get mode from survey or default to Landscape
  const mode = survey?.pdfPaperSize === "Portrait" ? "Portrait" : "Landscape";

  // Maximum lease comps to display per page based on mode
  // Portrait: 4 columns, Landscape: 7 columns
  const maxLeaseComps = mode === "Portrait" ? 4 : 7;

  // Filter hidden lease comps
  const leaseComps = (rawLeaseComps || []).filter((lc) => !lc.isHidden);

  // Filter and sort visible fields (isActive === true)
  const visibleFields = allFieldRules
    .filter((field) => field.isActive)
    .sort((a, b) => a.order - b.order);

  // Build lease comp order numbers for display badges
  const leaseCompOrders: Record<string, string> = {};
  leaseComps.forEach((lc, index) => {
    leaseCompOrders[lc.leaseCompId] = String(index + 1);
  });

  const showPropertyPhoto = survey?.showPropertyPhoto ?? true;

  return {
    leaseComps,
    visibleFields,
    maxLeaseComps,
    leaseCompOrders,
    mode,
    showPropertyPhoto,
  };
};
